import React, { SyntheticEvent, useState, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import cuid from 'cuid';
import { Box, Typography } from '@mui/material';
import { PostArtifact } from '@models/note';
import { FileUpload, PenSolid, Times } from '@assets/svgs';
import { Button } from '@components/button';
import { AddFilesDialog, ConfirmationDialog, MarkSubjectivityCompleteDialog } from '@components/dialog';
import { IMenuItem, MenuItemType } from '@components/menu';
import { useQuote } from '@services/quotes';
import { quoteStatusTerminal } from '@models/quotes';
import { acceptedFileMimeTypes } from '@utils/fileUtils';
import { uploadFile, useCreateFile, useDeleteFile } from '@services/file';
import { documentQueryKey, useCreateDocument, useDeleteDocument } from '@services/document';
import { DocumentType } from '@models/document';
import { useQueryClient } from '@tanstack/react-query';
import { IconButton } from '@components/iconButton';
import { SidebarCard } from '@pages/QuoteDetails/sidebarItems/SidebarCard';
import invariant from 'tiny-invariant';
import { ellipsis } from '@theme/styles';
import { SubjectivitySearchResponse } from '@lio/api-types';
import { ArrayItem } from '@utils/commonTypes';
import { useUpdateSubjectivity } from '@services/subjectivity/useUpdateSubjectivity';
import { useDeleteSubjectivity } from '@services/subjectivity/useDeleteSubjectivity';
import { SubjectivityStatus, SubjectivityStyle } from '@lio/rule-engine';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Tooltip } from '@components/tooltip/Tooltip';

const SubjectivityFormModal = lazy(() =>
  import('@pages/QuoteDetails/sidebarItems/subjectivities/SubjectivityFormModal/SubjectivityFormModal').then((m) => ({
    default: m.SubjectivityFormModal,
  })),
);

export type SubjectivityItem = ArrayItem<SubjectivitySearchResponse['items']>;

type SubjectivitiesItemProps = {
  item: SubjectivityItem;
  onClick?: () => void;
  isBroker?: boolean;
};

enum Action {
  DeleteSubjectivity = 'DeleteSubjectivity',
}

enum ItemTypeToDelete {
  Subjectivity = 'Subjectivity',
  File = 'File',
}

const subjectivitiesMenuItems: IMenuItem[] = [
  {
    type: MenuItemType.Default,
    text: 'Delete',
    actionName: Action.DeleteSubjectivity,
  },
];

export const SubjectivitiesItem = ({ isBroker, item, onClick }: SubjectivitiesItemProps) => {
  const { status, dueDaysAfterPolicyEffective, label, description, offeringSubjectivityId, documents, id } = item;
  const { quoteId } = useParams<'quoteId'>();
  invariant(quoteId);
  const { quote } = useQuote(quoteId);
  const client = useQueryClient();
  const { createDocumentAsync } = useCreateDocument();
  const { deleteFileAsync } = useDeleteFile();
  const { deleteDocumentAsync } = useDeleteDocument();
  const { createFileAsync } = useCreateFile();
  const { deleteSubjectivity } = useDeleteSubjectivity(quoteId);
  const { updateSubjectivity } = useUpdateSubjectivity(quoteId);
  const [{ isInternalUser, isLioSales }] = useStore((state) => [state.userAuth], shallow);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isMarkCompleteDialogOpen, setIsMarkCompleteDialogOpen] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string>();
  const [confirmDialogType, setConfirmationDialogType] = useState<ItemTypeToDelete | undefined>(undefined);

  const isDeleted = item.deletedAt ? true : false;
  const isCardDisabled = isDeleted || (!!quote?.status.name && quoteStatusTerminal.includes(quote?.status.name));
  const statusText = status === SubjectivityStatus.Active ? 'Missing Subjectivity' : 'Closed';

  const handleDeleteSubjectivity = () => {
    deleteSubjectivity(id);
  };

  const handleDeleteFile = async () => {
    setConfirmationDialogType(undefined);
    if (selectedDocId) {
      await deleteDocumentAsync({ documentId: selectedDocId });
      if (documents.length <= 1) {
        await updateSubjectivity({ status: SubjectivityStatus.Active, subjectivityId: id });
      }
    }
  };

  const actionMap = {
    [Action.DeleteSubjectivity]: () => {
      setConfirmationDialogType(ItemTypeToDelete.Subjectivity);
    },
  };

  const handleConfirmDelete = () => {
    handleDeleteSubjectivity();
    setConfirmationDialogType(undefined);
  };

  const actionItems = !offeringSubjectivityId
    ? subjectivitiesMenuItems.map((it) => {
        return {
          ...it,
          action: it.actionName ? actionMap[it.actionName as Action] : it.action,
          disabled: isCardDisabled,
        };
      })
    : undefined;

  const handleCloseFileModal = (e?: SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsUploadModalOpen(false);
  };

  const onAddNewFile = async (files: PostArtifact[]) => {
    await Promise.all(
      files
        .filter((f) => acceptedFileMimeTypes.includes(f.file.type))
        .map(async (f) => {
          let createdFileId;
          let createdDocumentId;
          try {
            const documentId = cuid();
            const file = await createFileAsync({
              description: f.description,
              awsS3Id: `document/${documentId}`,
              fileName: f.name,
              originalFileName: f.name,
              fileType: f.mimetype,
            });
            if (!file.uploadUrl) {
              console.log('Failed to create valid file, should delete it');
              throw new Error('Invalid file generated');
            }
            createdFileId = file.id;
            await uploadFile(file.uploadUrl, f.file);
            const document = await createDocumentAsync({
              fileId: file.id,
              subjectivityId: id,
              documentTypeId: DocumentType.Other.id,
            });
            createdDocumentId = document.id;
            return document.id;
          } catch (e) {
            console.log(e);
            if (createdFileId) {
              await deleteFileAsync({ fileId: createdFileId });
            }
            if (createdDocumentId) {
              await deleteDocumentAsync({ documentId: createdDocumentId });
            }
            return undefined;
          }
        }),
    );
    if (documents.length <= 1) {
      await updateSubjectivity({ status: SubjectivityStatus.Closed, subjectivityId: id });
    }
    client.refetchQueries({ queryKey: documentQueryKey.search });
  };

  const handleOpenMarkAsCompleteDialog = (e?: SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsMarkCompleteDialogOpen(true);
  };

  const handleOpenFileModal = (e?: SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    setIsUploadModalOpen(true);
  };

  const handleOpenFormDialog = (e?: SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    setIsFormDialogOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: isDeleted ? 'background.main' : 'inherit' }}>
      <SidebarCard
        title={label}
        statusText={statusText}
        status={status.toLowerCase()}
        cardType='SubjectivityItem'
        isDisabled={isCardDisabled}
        onClick={!isCardDisabled ? onClick : undefined}
        actionItems={actionItems}
        hideDelete={isBroker && !item.isBrokerSubjectivity}
      >
        {isDeleted ? (
          <Typography
            variant='caption'
            color='error.main'
            sx={{
              lineHeight: '14px',
              paddingTop: '15px',
            }}
          >
            This subjectivity is no longer valid because the related Line of Insurance has been removed from the quote
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                paddingBottom: '5px',
              }}
            >
              <Typography variant='caption'>
                due {dueDaysAfterPolicyEffective} days after Policy Effective Date
              </Typography>

              {item.bypassed ? (
                <Tooltip
                  title={
                    isInternalUser && item.completedByUser
                      ? `<b>Marked as Complete by:</b> ${item.completedByUser.firstName} ${item.completedByUser.lastName}${
                          item?.bypassReason ? `\n<b>Reason</b>: ${item.bypassReason}` : ''
                        }`
                      : ''
                  }
                  config={{
                    allowWhiteSpace: true,
                    applyMarkup: true,
                  }}
                >
                  <Typography
                    variant='bodySmall'
                    fontWeight='bold'
                    sx={{
                      textAlign: 'center',
                      pt: 1,
                      color: 'text.subdued',
                    }}
                  >
                    Marked as Complete
                  </Typography>
                </Tooltip>
              ) : (
                description && (
                  <Typography
                    variant='caption'
                    sx={{
                      lineHeight: '14px',
                      paddingTop: '15px',
                    }}
                  >
                    {description}
                  </Typography>
                )
              )}
              {documents.length > 0 &&
                documents.map((doc) => {
                  return (
                    <Box
                      key={doc.id}
                      sx={{
                        width: '100%',
                      }}
                    >
                      <Button
                        key={doc.id}
                        variant='text'
                        sx={{
                          ...ellipsis,
                          justifyContent: 'left',
                          paddingLeft: 0,
                          paddingRight: 0,
                          maxWidth: '85%',
                          display: 'inline-block',
                        }}
                        href={doc?.file?.downloadUrl ?? ''}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {doc?.file?.fileName}
                      </Button>
                      <IconButton
                        title='deleteSubjectivityDocument'
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmationDialogType(ItemTypeToDelete.File);
                          setSelectedDocId(doc.id);
                        }}
                        sx={{
                          width: '20px',
                          height: '20px',
                          padding: '5px',
                          marginLeft: '4px',
                          color: 'text.subdued',
                        }}
                      >
                        <Times />
                      </IconButton>
                    </Box>
                  );
                })}
            </Box>
            {item.subjectivityType?.style === SubjectivityStyle.Form ? (
              <Button
                variant='outlined'
                sx={{ width: '100px', marginTop: '10px', marginLeft: '-15px', alignSelf: 'center' }}
                startIcon={<PenSolid width='14px' height='14px' />}
                onClick={handleOpenFormDialog}
                disabled={isCardDisabled}
              >
                Edit
              </Button>
            ) : (
              <>
                <Button
                  variant='outlined'
                  sx={{ width: '100px', marginTop: '10px', marginLeft: '-15px', alignSelf: 'center' }}
                  startIcon={<FileUpload width='14px' height='14px' />}
                  onClick={handleOpenFileModal}
                  disabled={isCardDisabled}
                >
                  Upload
                </Button>
                {isInternalUser && !isLioSales && item.status === SubjectivityStatus.Active && (
                  <Button
                    variant='text'
                    sx={{ width: '100%', marginTop: '10px', marginLeft: '-15px', alignSelf: 'center' }}
                    onClick={handleOpenMarkAsCompleteDialog}
                    disabled={isCardDisabled}
                  >
                    Mark As Complete
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </SidebarCard>
      <ConfirmationDialog
        isOpen={confirmDialogType !== undefined}
        description={`Are you sure you want to delete this ${confirmDialogType}?`}
        onCancel={() => {
          setConfirmationDialogType(undefined);
        }}
        buttonConfig={{
          confirmText: 'Yes',
          secondaryText: 'No',
          onConfirm: confirmDialogType === ItemTypeToDelete.File ? handleDeleteFile : handleConfirmDelete,
        }}
      />
      <AddFilesDialog
        open={isUploadModalOpen}
        handleCloseDialog={handleCloseFileModal}
        onDone={({ uploadedFiles }) => onAddNewFile(uploadedFiles)}
      />
      {isMarkCompleteDialogOpen && quote && (
        <MarkSubjectivityCompleteDialog
          isOpen={isMarkCompleteDialogOpen}
          subjectivity={item}
          transactionNumber={quote?.policyNumber?.policyNumber || ''}
          onClose={() => {
            setIsMarkCompleteDialogOpen(false);
          }}
        />
      )}
      <Suspense fallback={null}>
        {isFormDialogOpen && item.subjectivityType && (
          <SubjectivityFormModal
            isOpen={true}
            subjectivity={item}
            title={`Quote #${quote?.policyNumber?.policyNumber} ${item?.label}`}
            quoteId={quoteId}
            onClose={() => setIsFormDialogOpen(false)}
          />
        )}
      </Suspense>
    </Box>
  );
};
